import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';
import { SortableHandle as sortableHandle } from 'react-sortable-hoc';

import { VerticalEllipsisIcon } from '../../../assets/Svg';
import { contentTypeIcon } from '../../../components/StoryItem';
import { IOnboardingFlowLaneMapper, StoryType } from '../../../services/types';

interface IProps {
  flowLaneMapper: Partial<IOnboardingFlowLaneMapper>;
  isEditing: boolean;
  onChange: (mapper: Partial<IOnboardingFlowLaneMapper>) => void;
  onDelete: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    minHeight: 190,
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    padding: 20,
    borderBottom: '1px solid #dcdcdc',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  thumbWrap: {
    flex: '0 0 68px',
    alignSelf: 'flex-start',
  },
  infoWrap: {
    flex: '0 0 314px',
    alignSelf: 'flex-start',
    paddingRight: 20,
    minWidth: 0,
  },
  infoLabel: {
    marginBottom: 6,
    fontSize: 12,
    lineHeight: '16px',
    color: 'rgba(0, 0, 0, 0.38)',
  },
  infoText: {
    fontSize: 14,
    marginBottom: 10,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  infoItem: {
    height: 68,
  },
  dragButton: {
    position: 'absolute',
    left: -5,
  },
  deleteButton: {
    marginLeft: 20,
    cursor: 'pointer',
  },
  coverImg: {
    width: 52,
    height: 76,
    objectFit: 'cover',
    borderRadius: 6,
  },
  storiesWrap: {
    alignSelf: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  storyListContainer: {},
  storyList: {
    width: '100%',
    padding: 10,
    maxHeight: 113,
    boxSizing: 'border-box',
    border: 'solid 1px #dcdcdc',
    lineHeight: '24px',
    overflow: 'hidden',
    overflowY: 'auto',
  },
  storyItem: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const DragHandle = sortableHandle(() => <VerticalEllipsisIcon />);

const FlowItem = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { flowLaneMapper, isEditing, onChange, onDelete } = props;

  const handleDeleteClick = () => {
    onDelete();
  };

  return (
    <Box className={classes.container}>
      {isEditing && (
        <span className={classes.dragButton}>
          <DragHandle />
        </span>
      )}
      <Box className={classes.thumbWrap}>
        <img
          src={
            flowLaneMapper.lane!.thumbUrl
              ? `${process.env.CDN_URL}/${flowLaneMapper.lane!.thumbUrl}?w=150`
              : `${process.env.CDN_URL}/static/cms/storycover-empty-v.png`
          }
          className={classes.coverImg}
        />
      </Box>
      <Box className={classes.infoWrap}>
        <Typography className={classes.infoLabel}>Segment ID</Typography>
        <Typography className={classes.infoText}>{flowLaneMapper.lane?.segmentId || '-'}</Typography>
        <Typography className={classes.infoLabel}>Lane Title</Typography>
        <Typography className={classes.infoText}>{flowLaneMapper.lane?.title}</Typography>
        <Typography className={classes.infoLabel}>Theme Name</Typography>
        <Typography className={classes.infoText}>{flowLaneMapper.lane?.themeName}</Typography>
      </Box>
      <Box className={classes.storiesWrap}>
        <Box className={classes.storyListContainer}>
          <Typography className={classes.infoLabel}>Mature Stories</Typography>
          <Box className={classes.storyList}>
            {flowLaneMapper.lane?.matureCollectionMappers.map((mapper, i) => (
              <Box className={classes.storyItem} key={`mapper-mature-${i}`}>
                {contentTypeIcon(mapper.collection?.stories[0]?.type)} ({mapper.themeName}) {mapper.collection?.stories[0]?.title}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className={classes.storyListContainer} marginTop={'10px'}>
          <Typography className={classes.infoLabel}>Nonmature Stories</Typography>
          <Box className={classes.storyList}>
            {flowLaneMapper.lane?.nonMatureCollectionMappers.map((mapper, i) => (
              <Box className={classes.storyItem} key={`mapper-nonmature-${i}`}>
                {contentTypeIcon(mapper.collection?.stories[0]?.type)} ({mapper.themeName}) {mapper.collection?.stories[0]?.title}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box></Box>
      {isEditing && (
        <span className={classes.deleteButton}>
          <DeleteIcon onClick={handleDeleteClick} color={'primary'} />
        </span>
      )}
    </Box>
  );
};

export default FlowItem;
